import {
  StyledAltButton,
  StyledTextButton,
} from "../../../../../../styles/ButtonStyles";
import {
  StyledFlexColumn,
  StyledFlexRow,
} from "../../../../../../styles/LayoutStyles";
import { useLanguageContext } from "../../../../../../context/LanguageContext";
import Draggable from "../../../../../../components/draggable/Draggable";
import { StyledField, StyledHeader, StyledRow } from "./FieldsFormStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "styled-components";
import Select from "../../../../../../components/select/main/Select";
import MultiSelect from "../../../../../../components/select/main/multi/MultiSelect";
import { StyledInputError } from "../../../../../../components/input/wrapper/InputWrapperStyles";
import EVAApiHelper from "../../../../../../helpers/EVAApiHelper";
import CompactError from "../../../../../../components/error/compact/CompactError";
import InputWrapper from "../../../../../../components/input/wrapper/InputWrapper";

function FieldsFormView({
  relations,
  selectedStep,
  selectedStepIndex,
  onAddRow,
  onDeleteRow,
  defaultFields,
  fieldOptions,
  onAddField,
  onDeleteField,
  onFieldOptionsChange,
  onFieldConditionChange,
  conditionKeys,
  conditionEffects,
  onDragEnter,
  submitError,
}) {
  const theme = useTheme();
  const { translate, translateEntity } = useLanguageContext();

  return (
    <StyledFlexColumn style={{ gap: theme.spacing.large }}>
      {/* Rows */}
      {selectedStep.fields?.map((row, rowIndex) => (
        <Draggable
          key={rowIndex}
          id={`FIELD_${rowIndex}`}
          onTargetEnter={onDragEnter}
        >
          <StyledRow>
            <StyledFlexColumn>
              {/* Row header */}
              <StyledFlexRow
                style={{
                  justifyContent: "space-between",
                  flexWrap: "nowrap",
                }}
              >
                <StyledFlexRow style={{ flexWrap: "nowrap" }}>
                  <StyledHeader>{`${translate("eva.main.general.row")} ${
                    rowIndex + 1
                  }`}</StyledHeader>

                  <CompactError
                    error={EVAApiHelper.getResponseError(
                      `steps.${selectedStepIndex}.fields.${rowIndex}`,
                    )}
                  />
                </StyledFlexRow>

                <StyledFlexRow>
                  {/* Row delete button */}
                  <StyledTextButton
                    onClick={() => onDeleteRow(rowIndex)}
                    title={translate("eva.main.entities.states.delete", [
                      {
                        key: "entities",
                        value: translate("eva.main.general.row"),
                      },
                    ])}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </StyledTextButton>

                  {/* Add Field Select */}
                  <Select
                    title={translate("eva.main.entities.states.create", [
                      {
                        key: "entities",
                        value: translate("eva.main.entities.field"),
                      },
                    ])}
                    as={StyledTextButton}
                    hideCaret={true}
                    valueVisual={(value) => <FontAwesomeIcon icon={faPlus} />}
                    onChange={(value) => onAddField(rowIndex, value)}
                    options={[
                      ...defaultFields.map((field) => ({
                        value: `DEFAULT_${field}`,
                        label: `${translate(
                          `eva.main.validation.attributes.${field}`,
                        )} (${translate("eva.main.general.default")})`,
                      })),
                      ...relations?.fields?.records?.map((field) => ({
                        value: field.id,
                        label: translateEntity(field, "label"),
                      })),
                    ]}
                  />
                </StyledFlexRow>
              </StyledFlexRow>

              <hr />

              {/* Row fields */}
              <StyledFlexColumn>
                {row.map((field, fieldIndex) => {
                  const fieldRecord = relations?.fields?.records?.find(
                    (f) => parseInt(f.id) === parseInt(field.field_id),
                  );

                  return (
                    <Draggable
                      key={`${rowIndex}_${fieldIndex}`}
                      id={`FIELD_${rowIndex},${fieldIndex}`}
                      onTargetEnter={onDragEnter}
                    >
                      <StyledField key={fieldIndex}>
                        <StyledFlexColumn>
                          {/* Field header */}
                          <StyledFlexRow
                            style={{
                              justifyContent: "space-between",
                              flexWrap: "nowrap",
                            }}
                          >
                            <StyledHeader>
                              {field.field_id
                                ? translateEntity(fieldRecord, "label")
                                : `${translate(
                                    `eva.main.validation.attributes.${field.default_field}`,
                                  )} (${translate(
                                    "eva.main.general.default",
                                  )})`}
                            </StyledHeader>

                            <StyledFlexRow>
                              {/* Field delete */}
                              <StyledTextButton
                                onClick={() =>
                                  onDeleteField(rowIndex, fieldIndex)
                                }
                                title={translate(
                                  "eva.main.entities.states.delete",
                                  [
                                    {
                                      key: "entities",
                                      value: translate(
                                        "eva.main.entities.field",
                                      ),
                                    },
                                  ],
                                )}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </StyledTextButton>
                            </StyledFlexRow>
                          </StyledFlexRow>

                          <hr />

                          <InputWrapper
                            error={EVAApiHelper.getResponseErrorsLike(
                              `steps.${selectedStepIndex}.fields.${rowIndex}.${fieldIndex}.`,
                              submitError,
                              translate,
                            )}
                          >
                            <MultiSelect
                              value={field.options}
                              onChange={(values) =>
                                onFieldOptionsChange(
                                  rowIndex,
                                  fieldIndex,
                                  values,
                                )
                              }
                              options={fieldOptions.map((option) => ({
                                value: option,
                                label: translate(
                                  `eva.main.validation.attributes.steps.*.fields.*.*.${option}`,
                                ),
                              }))}
                              placeholder={translate(
                                "eva.main.validation.attributes.options",
                              )}
                            />
                          </InputWrapper>

                          <InputWrapper
                            label={translate(
                              "eva.main.validation.attributes.steps.*.fields.*.*.condition_key",
                            )}
                          >
                            <Select
                              value={field.condition_key}
                              nullable
                              onChange={(value) =>
                                onFieldConditionChange(
                                  rowIndex,
                                  fieldIndex,
                                  "condition_key",
                                  value,
                                )
                              }
                              options={conditionKeys.map((key) => ({
                                value: key,
                                label: translate(
                                  `eva.main.flow.condition_keys.${key}`,
                                ),
                              }))}
                            />
                          </InputWrapper>

                          {field.condition_key && (
                            <>
                              <InputWrapper
                                label={translate(
                                  "eva.main.validation.attributes.steps.*.fields.*.*.condition_value",
                                )}
                              >
                                <Select
                                  value={field.condition_value}
                                  onChange={(value) =>
                                    onFieldConditionChange(
                                      rowIndex,
                                      fieldIndex,
                                      "condition_value",
                                      value,
                                    )
                                  }
                                  options={[
                                    {
                                      value: "1",
                                      label: translate("eva.main.general.yes"),
                                    },
                                    {
                                      value: "0",
                                      label: translate("eva.main.general.no"),
                                    },
                                  ]}
                                />
                              </InputWrapper>

                              <InputWrapper
                                label={translate(
                                  "eva.main.validation.attributes.steps.*.fields.*.*.condition_effect",
                                )}
                              >
                                <Select
                                  value={field.condition_effect}
                                  onChange={(value) =>
                                    onFieldConditionChange(
                                      rowIndex,
                                      fieldIndex,
                                      "condition_effect",
                                      value,
                                    )
                                  }
                                  options={conditionEffects.map((effect) => ({
                                    value: effect,
                                    label: translate(
                                      `eva.main.flow.condition_effects.${effect}`,
                                    ),
                                  }))}
                                />
                              </InputWrapper>
                            </>
                          )}
                        </StyledFlexColumn>
                      </StyledField>
                    </Draggable>
                  );
                })}
              </StyledFlexColumn>
            </StyledFlexColumn>
          </StyledRow>
        </Draggable>
      ))}

      {/* Row add button */}
      <StyledFlexColumn>
        <StyledAltButton onClick={onAddRow}>
          {translate("eva.main.entities.states.create", [
            { key: "entities", value: translate("eva.main.general.row") },
          ])}
        </StyledAltButton>

        <StyledInputError>
          {EVAApiHelper.getResponseError(
            `steps.${selectedStepIndex}.fields`,
            submitError,
          )}
        </StyledInputError>
      </StyledFlexColumn>
    </StyledFlexColumn>
  );
}

export default FieldsFormView;
